import { IconArrowRightSolid } from '@/app/common/icons/libraryIcons';
import { useCallback, useState } from 'react';
import { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '../../translations/Translation';
import { useIsMobile } from '../hooks/useMediaSSR';
import customCss from '../css/customCss';
import TSHExplanationDialog from './TSHExplanationDialog';

/**
 * `textVariant` sizes the explanation under the title: the legacy page sets it a step below its body
 * text, the Evo layout runs 16px throughout and asks for 'Body' so the banner matches (MPH-935).
 */
export default function TshInfoBanner({
  textVariant = 'BodySmall',
  ...props
}: { textVariant?: 'BodySmall' | 'Body' } & React.HTMLAttributes<HTMLElement>) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;
  /*
   * Important Note about the Dialog component:
   * When using the onClick event in a parent component of the Dialog, every click on the Dialog to
   * close it will trigger again the onClick in the parent forcing the dialog to open again.
   * The solutions are to put the Dialog outside the component with the onClick or to event.stopPropagation()
   * in the onClose handler event of the Dialog (in this case depending on the dialog properly propagating
   * the event element)
   */
  const [isExplanationOpen, setExplanationOpen] = useState(false);

  const openExplanationModal = useCallback(() => isMobile && setExplanationOpen(true), [isMobile]);

  return (
    <>
      <div
        css={css`
          display: flex;
          align-items: center;
          flex-direction: row;
          column-gap: 16px;
          background-color: ${(p) => p.theme.kallisto.color.common.special.topSecretHotels[200]};
          padding: 16px;
          color: ${(p) => p.theme.kallisto.color.common.special.topSecretHotels[900]};
        `}
        {...props}
        onClick={openExplanationModal}
        onKeyDown={openExplanationModal}
        role="button"
        tabIndex={0}
      >
        {}
        <img
          css={`
            width: 32px;
            height: 32px;
            align-self: center;

            @media ${customCss.breakpoints.sm} {
              width: 60px;
              height: 60px;
            }
          `}
          src={`${imageBasePath}logo-image-tsh.svg`}
          alt="tsh-icon-logo"
        />
        <div
          css={`
            flex-grow: 1;
          `}
        >
          <div
            css={`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <div
              css={css`
                ${({ theme }) => theme.kallisto.mixins.font('Title5')};
              `}
              /* eslint-disable-next-line react/no-danger */
              dangerouslySetInnerHTML={{ __html: filterXSS(t('vacation.select.details.tsh.collapsed')) }}
            />
            <IconArrowRightSolid
              css={css`
                color: ${({ theme }) => theme.kallisto.color.icon.static.iconPrimary};
                margin-left: 8px;
                min-height: 16px;
                min-width: 16px;
                @media ${customCss.breakpoints.sm} {
                  display: none;
                }
              `}
            />
          </div>
          {isMobile === false && (
            <Text
              variant={textVariant}
              css={css`
                color: ${({ theme }) => theme.kallisto.color.common.special.topSecretHotels[900]};
              `}
              dangerouslySetInnerHTML={{ __html: filterXSS(t('vacation.select.details.tsh.explanation')) }}
            />
          )}
        </div>
      </div>
      {isMobile && (
        <TSHExplanationDialog
          isOpen={isExplanationOpen}
          onClose={() => setExplanationOpen(false)}
          onOpenByBrowserHistory={() => setExplanationOpen(true)}
        />
      )}
    </>
  );
}
